import pluralize from 'pluralize';

import { type WorkspaceAgentMessagePill } from '~/components/v2/components/workspace/workspace-agent-panel/schemas/pillMetadata';
import {
  findWorkspaceAgentMessagePill,
  isUnresolvedLegacyCommentMoniker,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/findWorkspaceAgentMessagePill';

export type GetWorkspaceAgentPillCopyLabelOptions = {
  getAssetListMemberClipName?: (clipId: string) => string | undefined;
  getBoardTitle?: (boardId: string) => string | undefined;
  getPillClipName?: (pill: Extract<WorkspaceAgentMessagePill, { type: 'asset' }>) => string | undefined;
  pills: readonly WorkspaceAgentMessagePill[];
};

const getAssetListCopyLabel = (
  pill: Extract<WorkspaceAgentMessagePill, { type: 'assetList' }>,
  getAssetListMemberClipName: GetWorkspaceAgentPillCopyLabelOptions['getAssetListMemberClipName'],
): string => {
  const count = pill.count;
  const items = pill.items ?? [];

  if (items.length === 0) {
    return pluralize('asset', count, true);
  }

  const firstClipId = items[0]?.versionId;
  const firstName = firstClipId ? getAssetListMemberClipName?.(firstClipId) : undefined;

  if (count === 1) {
    return firstName ?? pluralize('asset', 1, true);
  }

  if (firstName) {
    return `${firstName} + ${count - 1} more`;
  }

  return pluralize('asset', count, true);
};

const getBoardListCopyLabel = (pill: Extract<WorkspaceAgentMessagePill, { type: 'boardList' }>): string =>
  pluralize('board', pill.count, true);

/** Plain-text label for a resolved pill when copying assistant markdown. */
export const getWorkspaceAgentPillCopyLabel = (
  pill: WorkspaceAgentMessagePill,
  { getAssetListMemberClipName, getBoardTitle, getPillClipName }: Omit<GetWorkspaceAgentPillCopyLabelOptions, 'pills'>,
): string => {
  switch (pill.type) {
    case 'asset':
      return getPillClipName?.(pill) ?? 'Asset';
    case 'assetList':
      return getAssetListCopyLabel(pill, getAssetListMemberClipName);
    case 'board':
      return getBoardTitle?.(pill.boardId) ?? 'Board';
    case 'boardList':
      return getBoardListCopyLabel(pill);
    case 'comment':
      return pill.authorName.trim().length > 0 ? `Comment from ${pill.authorName}` : 'Comment';
    case 'changePreview':
      return 'Change preview';
    case 'table':
      return pill.title.length > 0 ? pill.title : 'Table';
    case 'contextSourceFieldValue':
      return 'Brand kit image';
    default:
      return 'Reference';
  }
};

/** Resolves a moniker id to the copy label using message pill metadata. */
export const getWorkspaceAgentMonikerCopyLabel = ({
  getAssetListMemberClipName,
  getBoardTitle,
  getPillClipName,
  monikerId,
  pills,
}: GetWorkspaceAgentPillCopyLabelOptions & { monikerId: string }): string => {
  const pill = findWorkspaceAgentMessagePill(pills, monikerId);

  if (pill !== undefined) {
    return getWorkspaceAgentPillCopyLabel(pill, {
      getAssetListMemberClipName,
      getBoardTitle,
      getPillClipName,
    });
  }

  if (isUnresolvedLegacyCommentMoniker(monikerId, pill)) {
    return 'Comment';
  }

  return 'Asset';
};
